import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { FBoxContainer, FHConfirmDialog } from '../../../components/base';
import PageContainer from '../../../components/layout/PageContainer';
import { history } from '../../../helpers';
import { AdminPageTabs, Themes } from '../../../constants';
import { DAYS_OF_WEEK } from './constants';
import { useEmailCronConfigDetails } from './hooks';

const EmailCronConfigDetailsPage = () => {
  const { configId } = useParams();
  const {
    current,
    preview,
    previewConfigId,
    loadConfig,
    clearConfig,
    saveConfig,
    previewConfig,
    removeConfig,
  } = useEmailCronConfigDetails();
  const previewRef = useRef<HTMLDivElement>(null);

  const [subject, setSubject] = useState('');
  const [recipients, setRecipients] = useState('');
  const [errorRecipient, setErrorRecipient] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const loaded = current && Number(current.id) === Number(configId);

  useEffect(() => {
    // Reset local form state when navigating to a different config so a stale
    // `current` (a previously-viewed config) can't linger in the form.
    setSubject('');
    setRecipients('');
    setErrorRecipient('');
    setHtmlBody('');
    setDaysOfWeek([]);
    // Clear any stale `current`/`preview` from a previously-viewed config so a
    // late getById/preview response (or a flash of the prior config's data,
    // including PHI in the preview) can't render under this config's URL.
    clearConfig();
    if (configId) loadConfig(configId);
    return () => clearConfig();
  }, [configId, clearConfig, loadConfig]);

  useEffect(() => {
    // Only hydrate the form from `current` when it matches the config in the URL.
    if (current && String(current.id) === String(configId)) {
      setSubject(current.subject || '');
      setRecipients((current.recipients || []).join(', '));
      setErrorRecipient(current.error_notification_recipient || '');
      setHtmlBody(current.html_body || '');
      setDaysOfWeek(current.days_of_week || []);
    }
  }, [current, configId]);

  useEffect(() => {
    // The preview renders below a potentially tall HTML-body editor, so bring it
    // into view when a preview for the current config lands.
    if (preview && String(previewConfigId) === String(configId)) {
      previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [preview, previewConfigId, configId]);

  const buildPayload = () => ({
    subject: subject || null,
    recipients: recipients.trim()
      ? recipients
          .split(',')
          .map((r) => r.trim())
          .filter((r) => r)
      : null,
    error_notification_recipient: errorRecipient || null,
    html_body: htmlBody || null,
    days_of_week: daysOfWeek.length ? daysOfWeek : null,
  });

  const onSave = () => {
    // Defensively prevent saving while the correct config hasn't loaded yet,
    // so we never PATCH config B's URL with config A's stale form values.
    if (!loaded || !configId) return;
    saveConfig(configId, buildPayload());
  };

  const onPreview = () => {
    if (!configId) return;
    previewConfig(configId, { subject, html_body: htmlBody || null });
  };

  const onDelete = async () => {
    if (!configId) return;
    const ok = await removeConfig(configId);
    setShowDeletePopup(false);
    if (ok) {
      // The standalone list route was removed; land back on the Dashboard with
      // the Email Reports tab active (Dashboard reads `activeTab` from the URL query).
      history.navigate(`/admin?activeTab=${AdminPageTabs.EmailReportsTab}`);
    }
  };

  return (
    <PageContainer>
      <Box sx={{ mt: 3 }} textAlign="left">
        <Typography variant="h4" sx={{ mb: 2 }}>
          {/* Only show the matched config's job_key; never a non-matching
              (possibly stale) `current`'s title. */}
          {loaded ? current?.job_key : 'Email Cron Config'}
        </Typography>

        {!loaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FBoxContainer sx={{ mt: 2, boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
              />
              <TextField
                label="Recipients (comma-separated)"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                fullWidth
              />
              <TextField
                label="Error notification recipient"
                value={errorRecipient}
                onChange={(e) => setErrorRecipient(e.target.value)}
                fullWidth
              />

              <Box>
                <Typography sx={{ mb: 1 }}>
                  Days to send (none selected = every day; time of day is fixed):
                </Typography>
                <ToggleButtonGroup
                  value={daysOfWeek}
                  onChange={(_e, next: string[]) => setDaysOfWeek(next)}
                >
                  {DAYS_OF_WEEK.map((d) => (
                    <ToggleButton key={d} value={d}>
                      {d}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <TextField
                label="HTML body"
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                multiline
                minRows={10}
                maxRows={18}
                fullWidth
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={onSave} variant="contained" disabled={!loaded}>
                  Save
                </Button>
                <Button onClick={onPreview} variant="outlined" disabled={!loaded}>
                  Preview
                </Button>
                <Button onClick={() => setShowDeletePopup(true)} color="error">
                  Delete
                </Button>
              </Box>
            </Box>
          </FBoxContainer>
        )}

        {/* Only render the preview behind the same loaded-config gate AND when
            the preview was generated for the config currently on screen, so a
            late preview response from a previous config can't render its
            (possibly PHI-bearing) HTML under the next config. */}
        {loaded && preview && String(previewConfigId) === String(configId) && (
          <FBoxContainer sx={{ mt: 3, boxShadow: 'none' }}>
            <Box ref={previewRef} sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Preview — subject: {preview.subject_rendered}
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Recipients: {preview.recipients_resolved.join(', ')}{' '}
                {preview.has_data ? '' : '(no live data)'}
              </Typography>
              <iframe
                title="email-preview"
                sandbox=""
                style={{ width: '100%', height: 500, border: '1px solid #ccc' }}
                srcDoc={preview.html_rendered}
              />
            </Box>
          </FBoxContainer>
        )}
      </Box>

      <FHConfirmDialog
        open={showDeletePopup}
        colorTheme={Themes.Primary}
        title="Delete Email Cron Config"
        content="Delete this email cron config? The job will stop sending. This action cannot be undone."
        submittext="Yes, Delete"
        canceltext="Cancel"
        onClose={() => setShowDeletePopup(false)}
        onSubmit={onDelete}
      />
    </PageContainer>
  );
};

export default EmailCronConfigDetailsPage;
